import { formatDateValue } from './date-formatting.js';
import { formatNumberValue } from './number-formatting.js';
import { formatTextValue } from './text-formatting.js';
import { normalizeColumnFormat, normalizeMappingOverride, } from './column-format-spec.js';
/**
 * Returns true when a text spec carries no transform options. An empty text spec
 * must pass the raw value through unchanged so that legacy `dataType:'string'`
 * columns (folded to `{kind:'text'}`) do not coerce null→'' or numbers→strings.
 */
function isEmptyTextSpec(spec) {
    return !spec.trim && !spec.case && spec.maxLength == null && !spec.pad;
}
/**
 * Returns true when a number spec carries no format options. An empty number
 * spec must pass the raw value through unchanged so that legacy `dataType:'number'`
 * columns (folded to `{kind:'number'}`) do not coerce '1234.50'→'1234.5' or
 * null→''.
 */
function isEmptyNumberSpec(spec) {
    return spec.decimals == null && !spec.thousandsSeparator && !spec.currencySymbol && !spec.negativeStyle;
}
/**
 * Applies a resolved ColumnFormatSpec to a single raw value. Single point of
 * truth for type-aware rendering — used by the worker resolver and preview.
 *
 * Empty text/number specs (no options set) pass the raw value through unchanged.
 * This preserves the behaviour of legacy columns that were previously handled as
 * `raw` passthrough before EX-2 introduced `normalizeColumnFormat` folding.
 */
export function resolveFormatSpecValue(spec, raw) {
    switch (spec.kind) {
        case 'text':
            if (isEmptyTextSpec(spec))
                return raw;
            return formatTextValue(raw, spec);
        case 'date':
            return formatDateValue(raw, spec.token);
        case 'number':
            if (isEmptyNumberSpec(spec))
                return raw;
            return formatNumberValue(raw, spec);
        case 'raw':
        default:
            return raw;
    }
}
/**
 * Computes the per-form effective columns: each column's canonical format with
 * its matching mapping's whole-spec override overlaid (matched by targetColumn).
 * Both the worker export jobs and the preview renderer consume this so runtime
 * output and preview can never drift.
 */
export function resolveEffectiveColumns(columns, mappings) {
    const overrideByColumn = new Map();
    for (const m of mappings) {
        const override = normalizeMappingOverride(m);
        if (override)
            overrideByColumn.set(m.targetColumn, override);
    }
    return columns.map((col) => {
        const spec = overrideByColumn.get(col.name) ?? normalizeColumnFormat(col);
        return { ...col, format: spec, dataType: spec.kind };
    });
}
//# sourceMappingURL=format-spec-resolver.js.map